import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import Router from 'next/router';
import { Box, Container, Grid } from '@mui/material';
import { isEmpty } from 'lodash';
import { AdminLayout } from '@components/AdminLayout';
import {
  AdminDashboardStatics,
  AdminDashboardSidebar,
  DashboardLoginActivity,
  AdminDashboardBreadCrumbs,
  AdminDashboardTopLearnings,
  AdminDashboardCollectionTypes,
  AdminDashboardLearningsDetails,
  DashboardLoginActivityUserDetails,
  AdminDashboardSelectedFilterChips,
  AdminDashboardCollectionTypeDetails,
  AdminDashboardHighestRatedLearnings,
} from '@components/Dashboard';
import ResizeSidebar from '@components/ResizeSidebar';
import SidebarDrawer from '@components/SidebarDrawer';
import { BUTTON_GROUP } from '@constants';
import { DashboardContext, eDashboardActionType } from '@contexts/dashboardContext';

const AdminDashboard = () => {
  const gridRef = useRef<HTMLDivElement>(null);
  const [defaultMinWidth, setDefaultMinWidth] = useState(240);
  const [openFilter, setOpenFilter] = useState(true);
  const [isChanged, setIsChanged] = useState(true);
  const {
    dispatch,
    state: {
      detailsPage,
      sideBarApiFilter: { checkedList, internalId1, selectedGroup },
    },
  } = useContext(DashboardContext);

  // get value of default sidebar width
  useEffect(() => {
    const secondGridWidth = gridRef.current?.clientWidth;
    if (secondGridWidth) {
      setDefaultMinWidth(secondGridWidth);
    }
  }, []);

  const isDetailsPageVisible = detailsPage !== null;

  const schoolData = useMemo(
    () =>
      internalId1
        .filter((item) => checkedList.some((el) => el.id === item.id))
        .map((val) => ({ id: val.internalId, title: val.title })),
    [checkedList, internalId1],
  );

  const groupData = useMemo(
    () =>
      selectedGroup.map((val, index) => ({
        id: val?._id || `${index + 1}`,
        title: val?.name || 'Untitled',
      })),
    [selectedGroup],
  );

  // TO RESET CONTEXT WHILE NAVIGATING
  useEffect(() => {
    const handleRouteChange = () => {
      dispatch({
        type: 'DETAILS_PAGE' as eDashboardActionType.DETAILS_PAGE,
        data: null,
      });
    };

    Router.events.on('routeChangeStart', handleRouteChange);
    return () => {
      Router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [dispatch]);

  const getDetailsReports = () => {
    switch (detailsPage?.type) {
      case 'LEARNINGS':
        return <AdminDashboardLearningsDetails />;
      case 'COLLECTION_TYPE':
        return <AdminDashboardCollectionTypes />;
      case 'COLLECTION_TYPE_DETAIL':
        return <AdminDashboardCollectionTypeDetails />;
      case 'LOGIN_ACTIVITY':
        return <DashboardLoginActivity />;
      case 'LOGIN_ACTIVITY_USER_DETAILS':
        return <DashboardLoginActivityUserDetails />;
      case 'TOP_LEARNINGS':
        return <AdminDashboardTopLearnings />;
      case 'HIGHEST_RATED':
        return <AdminDashboardHighestRatedLearnings />;
      default:
        return <Box />;
    }
  };

  return (
    <AdminLayout
      withSidebar
      titleIcon="DASHBOARD"
      titleViewBox="0 0 24 24"
      title={BUTTON_GROUP.DASHBOARD}
      breadCrumbsChildren={<AdminDashboardBreadCrumbs />}
      iconSx={{ fill: (theme) => theme.palette.white[100] }}>
      <Box sx={{ width: '100%' }}>
        <Grid container spacing={2}>
          {/* Sidebar filters in Drawer for small screen */}
          <SidebarDrawer>
            <AdminDashboardSidebar />
            <Box sx={{ paddingBottom: '10rem' }} />
          </SidebarDrawer>
          <Grid
            item
            xl={openFilter ? 2 : 1}
            md={openFilter ? 3 : 0.5}
            sm={12}
            xs={12}
            ref={gridRef}
            sx={{ display: { xs: 'none', md: 'block' }, minWidth: 240 }}>
            {/* Sidebar filters for large screen */}
            <ResizeSidebar
              defaultMinWidth={defaultMinWidth}
              openFilter={openFilter}
              isChanged={isChanged}
              setIsChanged={setIsChanged}
              handleFilter={(val) => {
                setOpenFilter(val);
              }}>
              <Box sx={{ display: openFilter ? 'block' : 'none' }}>
                <AdminDashboardSidebar
                  onShowResultClick={() => {
                    setIsChanged(true);
                  }}
                />
              </Box>
            </ResizeSidebar>
          </Grid>
          <Grid
            item
            xl={openFilter ? 8 : 9}
            md={openFilter ? 9 : 11.5}
            sm={12}
            xs={12}
            sx={{ mt: 2 }}>
            <Container maxWidth="lg">
              {/*  Show dashboard reports  */}
              {!isDetailsPageVisible && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* School Filter Chips  */}
                  <AdminDashboardSelectedFilterChips
                    data={schoolData}
                    show={
                      !isEmpty(internalId1) &&
                      checkedList.some((val) => internalId1.some((it) => it.id === val.id))
                    }
                    type="SCHOOL"
                    headerTitle="Selected School(s):"
                    summaryText="No schools selected"
                  />
                  {/* Group Filter Chips  */}
                  {!isEmpty(selectedGroup) && (
                    <AdminDashboardSelectedFilterChips
                      data={groupData}
                      type="GROUP"
                      show={!isEmpty(selectedGroup)}
                      headerTitle="Selected Groups(s):"
                      summaryText="No groups selected"
                    />
                  )}
                  <AdminDashboardStatics />
                </Box>
              )}
              {/*  Show detail reports  */}
              {isDetailsPageVisible && <Box>{getDetailsReports()}</Box>}
            </Container>
          </Grid>
          <Grid item xl={2} md={0} sm={3} xs={3} />
        </Grid>
      </Box>
    </AdminLayout>
  );
};

export default AdminDashboard;
